import React, { useMemo } from "react";
import { useCustomTranslation } from "../../../helpers/translateHelpers";
import { CustomerPortalIntervention, InterventionTableQuery } from "../models";
import { useGetInterventionsQuery } from "../queries";
import { Column } from "react-data-grid";
import dayjs from "dayjs";
import { getTablePageCount } from "../../../helpers/tableHelpers";
import {
  renderDate,
  renderDateTime,
  renderTime,
} from "../../../helpers/dayjsHeleprs";
import { useNavigate } from "../../../helpers/navigationHelpers";
import { Table } from "../../../components";

interface Props {
  setQueryParams: (params: InterventionTableQuery) => void;
  queryParams: InterventionTableQuery;
}

const InterventionsTable: React.FC<Props> = (props): React.ReactElement => {
  const { queryParams, setQueryParams } = props;

  const { tIntervention, tOrder } = useCustomTranslation();
  const navigate = useNavigate();

  const query = useGetInterventionsQuery(queryParams);

  const columns: Column<CustomerPortalIntervention>[] = useMemo(
    () => [
      {
        key: "orderCode",
        name: tOrder("code"),
        sortable: true,
      },
      {
        key: "state",
        name: tIntervention("state"),
        renderCell: (props) => tIntervention(`state_${props.row.state}`),
        sortable: true,
      },
      {
        key: "categoryName",
        name: tIntervention("category"),
        sortable: true,
      },
      {
        key: "orderCategoryName",
        name: tIntervention("orderCategory"),
        sortable: true,
      },
      {
        key: "oneLinerAddress",
        name: tOrder("address"),
        sortable: true,
      },
      {
        key: "assignations",
        name: tIntervention("assignations"),
        width: "200px",
        renderCell: ({ row }) => {
          const names = row.assignations.map((x) => x.fullName).join(", ");

          return (
            <div title={names} className="overflow-hidden text-ellipsis">
              {names}
            </div>
          );
        },
      },
      {
        key: "day",
        name: tIntervention("day"),
        renderCell: ({ row }) => renderDate(row.day),
        sortable: true,
      },
      {
        key: "planned_time",
        name: tIntervention("planned_time"),
        renderCell: ({ row }) => {
          const startTimes = row.assignations.map((assignation) =>
            dayjs.utc(assignation.plannedStart),
          );

          const endTimes = row.assignations.map((assignation) =>
            dayjs.utc(assignation.plannedEnd),
          );

          const plannedStart = dayjs.min(startTimes);
          const plannedEnd = dayjs.max(endTimes);

          return `${plannedStart?.format("LT")} - ${plannedEnd?.format("LT")}`;
        },
      },
      {
        key: "startedAt",
        name: tIntervention("real_time"),
        sortable: true,
        renderCell: ({ row }) => {
          if (row.startedAt && row.endedAt) {
            return `${renderDateTime(row.startedAt)} - ${renderTime(row.endedAt)}`;
          }

          return tIntervention("not_started");
        },
      },
    ],
    [tIntervention, tOrder],
  );

  return (
    <Table
      columns={columns}
      data={query.data?.data ?? []}
      loading={query.isLoading}
      queryParams={queryParams}
      onTableQueryChange={setQueryParams}
      pageCount={getTablePageCount(
        query.data?.total ?? 1,
        queryParams.pageSize,
      )}
      onRowClick={(item) => navigate.goTo(`orders/${item.orderId}`)}
      disableExtendToFullWidth
    />
  );
};

export default InterventionsTable;
